import {
    enrichAutocompleteResultsWithDerivedHits,
    resolveIndexKind,
} from './derive-hits-from-parts.js';

import { escapeHtml } from '../core/utilities.js';

export function getMeilisearchConfig() {
    return {
        host: ($('#meilisearch_host').val() || '').trim(),
        searchKey: ($('#meilisearch_search_key').val() || '').trim(),
    };
}

/**
 * Hyphens → spaces; cap at maxBytes UTF-8 so Meili never sees oversized queries.
 * @param {string} text
 * @param {number} [maxBytes=500]
 * @returns {string}
 */
export function normalizeAutocompleteQuery(text, maxBytes) {
    var query = String(text || '')
        .replace(/-/g, ' ')
        .trim();
    var limit =
        typeof maxBytes === 'number' && maxBytes > 0 ? maxBytes : 500;

    if (typeof TextEncoder === 'undefined') {
        return query.length > limit ? query.slice(0, limit) : query;
    }

    var encoder = new TextEncoder();
    if (encoder.encode(query).length <= limit) {
        return query;
    }

    var truncated = query;
    while (
        truncated.length &&
        encoder.encode(truncated).length > limit
    ) {
        truncated = truncated.slice(0, -1);
    }
    return truncated;
}

export function buildAutocompleteIndexNames(memberCode) {
    var normalizedMemberCode = (memberCode || '').trim();

    return {
        brands: 'PACE_' + normalizedMemberCode + '_brands',
        categories: 'PACE_' + normalizedMemberCode + '_categories',
        parts: 'PACE_' + normalizedMemberCode + '_parts',
        series: 'PACE_' + normalizedMemberCode + '_series',
    };
}

/**
 * Ensure imgix URLs used in autocomplete thumbnails request compression and a
 * sane max width (matches brand logo URLs like …?fit=max&w=192&auto=compress,format).
 * @param {string} url
 * @returns {string}
 */
export function normalizeImgixAutocompleteImageUrl(url) {
    var s = (url || '').trim();
    if (!s.length) {
        return '';
    }
    if (s.indexOf('imgix.net') === -1) {
        return s;
    }
    if (s.indexOf('auto=') !== -1) {
        return s;
    }
    var sep = s.indexOf('?') === -1 ? '?' : '&';
    return s + sep + 'fit=max&w=192&auto=compress,format';
}

// kept as a named export for the autocomplete callers; the implementation is the shared one
export function escapeAutocompleteHtml(value) {
    return escapeHtml(value);
}

export function sanitizeAutocompleteHref(href, origin) {
    var value = (href || '').trim();
    if (!value.length || value === '#') {
        return '#';
    }
    if (/[\s"<>]/.test(value)) {
        return '#';
    }
    if (value.charAt(0) === '#') {
        return '#';
    }
    if (value.charAt(0) === '/' && value.charAt(1) !== '/') {
        return value;
    }

    try {
        var baseOrigin = origin || window.location.origin;
        var parsed = new URL(value, baseOrigin);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return '#';
        }
        if (parsed.origin !== new URL(baseOrigin).origin) {
            return '#';
        }
        return parsed.pathname + parsed.search + parsed.hash;
    } catch (error) {
        return '#';
    }
}

export function sanitizeAutocompleteMediaUrl(url, origin) {
    var value = (url || '').trim();
    if (!value.length || /[\s"<>]/.test(value)) {
        return '';
    }

    try {
        var parsed = new URL(value, origin || window.location.origin);
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            return parsed.href;
        }
    } catch (error) {
        return '';
    }

    return '';
}

export function extractAutocompletePartIdentity(hit) {
    var rawObjectId = (hit && hit.objectID ? String(hit.objectID) : '').trim();

    if (!rawObjectId.length) {
        return {
            brandCode: '',
            partNumber: '',
        };
    }

    var separatorIndex = rawObjectId.indexOf('-');
    if (separatorIndex === -1) {
        return {
            brandCode: '',
            partNumber: rawObjectId,
        };
    }

    return {
        brandCode: rawObjectId.slice(0, separatorIndex),
        partNumber: rawObjectId.slice(separatorIndex + 1),
    };
}

export function extractAutocompleteCategoryCount(hit) {
    if (!hit) {
        return null;
    }
    var candidates = [
        hit.num_items,
        hit.item_count,
        hit.product_count,
        hit.count,
    ];
    for (var i = 0; i < candidates.length; i++) {
        var value = candidates[i];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        var num = parseInt(value, 10);
        if (!isNaN(num) && num > 0) {
            return num;
        }
    }
    return null;
}

export function extractAutocompletePartBrand(hit) {
    if (!hit) {
        return '';
    }
    var brand =
        hit.BrandLabel ||
        hit.BrandName ||
        hit.brand_label ||
        (hit.brand &&
            typeof hit.brand === 'object' &&
            (hit.brand.name || hit.brand.label)) ||
        (typeof hit.brand === 'string' ? hit.brand : '') ||
        hit.Brand ||
        '';
    return (brand || '').toString().trim();
}

function stripBrandPrefixFromText(text, brand) {
    var value = (text || '').toString().trim();
    var brandName = (brand || '').toString().trim();
    if (!value.length || !brandName.length) {
        return value;
    }
    if (value.toLowerCase() === brandName.toLowerCase()) {
        return '';
    }
    var brandPrefix = new RegExp(
        '^' +
            brandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
            '[\\s\\-–—:|]*',
        'i'
    );
    return value.replace(brandPrefix, '').trim();
}

export function extractAutocompletePartDescription(hit, brand, partNumber) {
    var paceData = hit && hit.pace ? hit.pace : {};
    var normalizedPart = (partNumber || '').toString().trim();
    var normalizedBrand = (brand || '').toString().trim();
    var candidates = [
        paceData.name,
        hit && hit.series,
        hit && hit.display_name,
        hit && hit.title,
        hit && hit.name,
        hit && hit.PartTerminologyName,
        hit && hit.description,
        hit && hit.short_description,
        paceData.description,
    ];

    for (var i = 0; i < candidates.length; i++) {
        var raw = candidates[i];
        if (raw === undefined || raw === null) {
            continue;
        }
        var text = String(raw).trim();
        if (!text.length) {
            continue;
        }
        if (
            normalizedPart &&
            text.toLowerCase() === normalizedPart.toLowerCase()
        ) {
            continue;
        }
        text = stripBrandPrefixFromText(text, normalizedBrand);
        if (!text.length) {
            continue;
        }
        if (
            normalizedPart &&
            text.toLowerCase() === normalizedPart.toLowerCase()
        ) {
            continue;
        }
        return text;
    }

    return '';
}

export function extractAutocompletePartPrice(hit) {
    var paceData = hit && hit.pace ? hit.pace : {};
    var candidates = [
        hit && hit.retail_price,
        hit && hit.jobber_price,
        hit && hit.price,
        hit && hit.list_price,
        hit && hit.ListPrice,
        paceData.price,
        paceData.retail_price,
    ];
    for (var i = 0; i < candidates.length; i++) {
        var value = candidates[i];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        var num =
            typeof value === 'number'
                ? value
                : parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
        if (!isNaN(num) && num > 0) {
            return (
                '$' +
                num.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                })
            );
        }
    }
    return null;
}

export function normalizeAutocompletePartHit(hit, origin, vaaq) {
    var paceData = hit && hit.pace ? hit.pace : {};
    var identity = extractAutocompletePartIdentity(hit);
    var brandSlug =
        (hit && (hit.brand_slug || hit.brandSlug || hit.brand_url_string)) ||
        (hit &&
            hit.brand &&
            (hit.brand.slug ||
                hit.brand.url_string ||
                hit.brand.URLString)) ||
        '';
    var partNumber =
        (hit &&
            (hit.Part ||
                hit.PartNumber ||
                hit.part ||
                hit.part_number ||
                hit.objectID)) ||
        identity.partNumber ||
        '';
    var slug = (paceData.slug || (hit && (hit.slug || hit.pace_slug)) || '').trim();
    var href = '#';

    if (slug) {
        href = origin + '/' + slug + vaaq;
    } else if (brandSlug && partNumber) {
        href =
            origin +
            '/catalog/item/' +
            brandSlug +
            '/' +
            encodeURIComponent(partNumber) +
            vaaq;
    }

    var rawImage =
        paceData.image ||
        (hit && (hit.image || hit.thumbnail || hit.pace_image)) ||
        '';

    var brand = extractAutocompletePartBrand(hit);
    var normalizedPartNumber = (partNumber || '').toString().trim();
    var description = extractAutocompletePartDescription(
        hit,
        brand,
        normalizedPartNumber
    );

    return {
        href: sanitizeAutocompleteHref(href, origin),
        image: sanitizeAutocompleteMediaUrl(
            normalizeImgixAutocompleteImageUrl(rawImage),
            origin
        ),
        label:
            description ||
            normalizedPartNumber ||
            (hit && hit.objectID) ||
            '',
        partNumber: normalizedPartNumber,
        brand: brand,
        description: description,
        price: extractAutocompletePartPrice(hit),
    };
}

export function dedupeAutocompleteHits(items, getKey) {
    var seen = {};

    return (items || []).filter(function (item, index) {
        var rawKey = getKey(item, index);
        var key = (rawKey || '').toString().trim();

        if (!key.length) {
            key = 'fallback-' + index;
        }

        if (seen[key]) {
            return false;
        }

        seen[key] = true;
        return true;
    });
}

export function parseAutocompleteRequestParams(params) {
    if (typeof params === 'string') {
        var parsed = {};
        var searchParams = new URLSearchParams(params);

        searchParams.forEach(function (value, key) {
            parsed[key] = value;
        });

        return parsed;
    }

    return Object.assign({}, params || {});
}

export function stringifyAutocompleteRequestParams(params, originalParams) {
    if (typeof originalParams === 'string') {
        return new URLSearchParams(params).toString();
    }

    return params;
}

export function buildEmptyAutocompleteResults(requests) {
    return {
        results: (requests || []).map(function (request) {
            var params = parseAutocompleteRequestParams(request.params);
            var hitsPerPage = parseInt(
                params.hitsPerPage || params.limit || 10,
                10
            );

            return {
                hits: [],
                nbHits: 0,
                page: parseInt(params.page || 0, 10) || 0,
                nbPages: 0,
                hitsPerPage: isNaN(hitsPerPage) ? 10 : hitsPerPage,
                processingTimeMS: 0,
                exhaustiveNbHits: false,
                query: params.query || '',
                params:
                    typeof request.params === 'string' ? request.params : '',
            };
        }),
    };
}

export var autocompleteSearchState = {
    instance: null,
    queryDebounceTimer: null,
};

export function createAutocompleteSearchClient(
    baseSearchClient,
    debounceMs,
    maxHitsPerCap
) {
    var pendingResolvers = [];
    var debounceTimer = null;
    var normalizedDebounceMs = parseInt(debounceMs, 10);
    var latestRequests = [];
    var activeAbortController = null;

    var CACHE_MAX = 30;
    var cacheKeys = [];
    var cacheMap = {};

    var hitsCap = 5;
    if (maxHitsPerCap !== undefined && maxHitsPerCap !== null) {
        var parsedCap = parseInt(maxHitsPerCap, 10);
        if (!isNaN(parsedCap) && parsedCap > 0) {
            hitsCap = parsedCap;
        }
    }

    if (isNaN(normalizedDebounceMs) || normalizedDebounceMs < 0) {
        normalizedDebounceMs = 200;
    }

    function buildCacheKey(requests) {
        return requests.map(function (r) {
            return (r.indexName || '') + ':' + (r._autocompleteQuery || '');
        }).join('|');
    }

    function cachePut(key, value) {
        if (cacheMap[key]) {
            return;
        }
        if (cacheKeys.length >= CACHE_MAX) {
            var evicted = cacheKeys.shift();
            delete cacheMap[evicted];
        }
        cacheKeys.push(key);
        cacheMap[key] = value;
    }

    function cacheGet(key) {
        return cacheMap[key] || null;
    }

    function resolvePendingWith(result) {
        pendingResolvers.forEach(function (pending) {
            pending.resolve(result);
        });
        pendingResolvers = [];
    }

    function rejectPendingWith(error) {
        pendingResolvers.forEach(function (pending) {
            pending.reject(error);
        });
        pendingResolvers = [];
    }

    function flushDebouncedAutocompleteSearch() {
        var requestsToSearch = latestRequests.map(function (request) {
            var nextRequest = Object.assign({}, request);
            delete nextRequest._autocompleteQuery;
            return nextRequest;
        });

        debounceTimer = null;

        var key = buildCacheKey(latestRequests);
        var cached = cacheGet(key);
        if (cached) {
            resolvePendingWith(cached);
            return;
        }

        if (activeAbortController) {
            activeAbortController.abort();
        }
        activeAbortController = typeof AbortController !== 'undefined'
            ? new AbortController()
            : null;

        var currentController = activeAbortController;

        baseSearchClient
            .search(requestsToSearch, { signal: currentController ? currentController.signal : undefined })
            .then(function (result) {
                if (currentController && currentController.signal.aborted) {
                    return;
                }
                cachePut(key, result);
                resolvePendingWith(result);
            })
            .catch(function (error) {
                if (currentController && currentController.signal.aborted) {
                    return;
                }
                rejectPendingWith(error);
            });
    }

    return {
        search: function (requests) {
            var normalizedRequests = (requests || []).map(function (request) {
                var params = parseAutocompleteRequestParams(request.params);
                var query = normalizeAutocompleteQuery(params.query || '');
                params.query = query;
                var cappedHitsPerPage = parseInt(
                    params.hitsPerPage || params.limit || 5,
                    10
                );

                if (isNaN(cappedHitsPerPage) || cappedHitsPerPage <= 0) {
                    cappedHitsPerPage = 5;
                }

                cappedHitsPerPage = Math.min(cappedHitsPerPage, hitsCap);
                params.hitsPerPage = cappedHitsPerPage;
                params.limit = cappedHitsPerPage;

                return Object.assign({}, request, {
                    params: stringifyAutocompleteRequestParams(
                        params,
                        request.params
                    ),
                    _autocompleteQuery: query,
                });
            });

            var hasNonEmptyQuery = normalizedRequests.some(function (request) {
                return request._autocompleteQuery.length > 0;
            });

            if (!hasNonEmptyQuery) {
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                    debounceTimer = null;
                }
                if (activeAbortController) {
                    activeAbortController.abort();
                    activeAbortController = null;
                }
                if (pendingResolvers.length) {
                    resolvePendingWith(
                        buildEmptyAutocompleteResults(
                            latestRequests.length ? latestRequests : normalizedRequests
                        )
                    );
                }
                latestRequests = [];
                return Promise.resolve(buildEmptyAutocompleteResults(normalizedRequests));
            }

            latestRequests = normalizedRequests;

            var key = buildCacheKey(normalizedRequests);
            var cached = cacheGet(key);
            if (cached) {
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                    debounceTimer = null;
                }
                if (activeAbortController) {
                    activeAbortController.abort();
                    activeAbortController = null;
                }
                if (pendingResolvers.length) {
                    resolvePendingWith(cached);
                }
                return Promise.resolve(cached);
            }

            return new Promise(function (resolve, reject) {
                pendingResolvers.push({
                    resolve: resolve,
                    reject: reject,
                });

                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                }

                debounceTimer = setTimeout(function () {
                    flushDebouncedAutocompleteSearch();
                }, normalizedDebounceMs);
            });
        },
    };
}

/**
 * Meilisearch multi-search client (same wire format as navbar autocomplete).
 *
 * `options.partsFacets` opts a caller into facets on its parts query. It is
 * opt-in rather than keyed off the index name because quick-order shares this
 * client and queries the same parts index without consuming facets — it should
 * not pay for them on every keystroke.
 *
 * @param {{ host: string, searchKey?: string }} config
 * @param {{ partsFacets?: string[] }} [clientOptions]
 */
export function createMeilisearchDirectSearchClient(config, clientOptions) {
    var meilisearchHost = (config.host || '').replace(/\/+$/, '');
    var meilisearchFetchHeaders = { 'Content-Type': 'application/json' };
    if (config.searchKey) {
        meilisearchFetchHeaders['Authorization'] =
            'Bearer ' + config.searchKey;
    }

    var partsFacets =
        clientOptions &&
        Array.isArray(clientOptions.partsFacets) &&
        clientOptions.partsFacets.length
            ? clientOptions.partsFacets
            : null;

    return {
        search: function (requests, options) {
            var queries = (requests || []).map(function (req) {
                var params = parseAutocompleteRequestParams(req.params);
                var query = normalizeAutocompleteQuery(params.query || '');
                var hitsPerPage = parseInt(params.hitsPerPage || 10, 10);
                if (isNaN(hitsPerPage) || hitsPerPage <= 0) hitsPerPage = 10;
                var page = parseInt(params.page || 0, 10);
                if (isNaN(page) || page < 0) page = 0;

                var meiliQuery = {
                    indexUid: req.indexName,
                    q: query,
                    hitsPerPage: hitsPerPage,
                    page: page + 1,
                    // Dropdown renders raw fields only; highlighting doubles payload.
                    attributesToHighlight: [],
                };

                if (
                    params.attributesToRetrieve &&
                    params.attributesToRetrieve.length
                ) {
                    meiliQuery.attributesToRetrieve =
                        params.attributesToRetrieve;
                }

                // InstantSearch Configure uses `filters`; Meili expects `filter`.
                var filterValue =
                    params.filter ||
                    params.filters ||
                    '';
                if (
                    typeof filterValue === 'string' &&
                    filterValue.trim().length
                ) {
                    meiliQuery.filter = filterValue.trim();
                }

                if (partsFacets && resolveIndexKind(req.indexName) === 'parts') {
                    meiliQuery.facets = partsFacets;
                }

                return meiliQuery;
            });

            var fetchOptions = {
                method: 'POST',
                headers: meilisearchFetchHeaders,
                body: JSON.stringify({ queries: queries }),
            };
            if (options && options.signal) {
                fetchOptions.signal = options.signal;
            }

            return fetch(meilisearchHost + '/multi-search', fetchOptions)
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error(
                            'Meilisearch search failed: ' + response.status
                        );
                    }
                    return response.json();
                })
                .then(function (data) {
                    var mappedResults = (data.results || []).map(function (
                        result,
                        i
                    ) {
                        var hits = (result.hits || []).map(function (hit) {
                            if (hit._formatted) {
                                // Highlighting is disabled; drop any leftover
                                // _formatted copy so payloads stay small.
                                delete hit._formatted;
                            }
                            return hit;
                        });

                        var mapped = {
                            hits: hits,
                            nbHits:
                                result.totalHits ||
                                result.estimatedTotalHits ||
                                0,
                            page: (result.page || 1) - 1,
                            nbPages: result.totalPages || 0,
                            hitsPerPage: result.hitsPerPage || 10,
                            processingTimeMS:
                                result.processingTimeMs || 0,
                            exhaustiveNbHits: true,
                            query: result.query || '',
                            params:
                                typeof requests[i].params === 'string'
                                    ? requests[i].params
                                    : '',
                        };

                        // Counts for the category rows are read off this below.
                        if (result.facetDistribution) {
                            mapped.facetDistribution =
                                result.facetDistribution;
                        }

                        return mapped;
                    });

                    enrichAutocompleteResultsWithDerivedHits(
                        requests,
                        mappedResults
                    );

                    return { results: mappedResults };
                });
        },
    };
}
